import React, { useState, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, 
  Trash2, 
  Plus, 
  AlertCircle,
  Clock,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newHoliday, setNewHoliday] = useState({ date: '', description: '', scope: 'general', professional_name: '' });
  const { addNotification } = useNotifications();

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('holidays')
        .select('*')
        .order('holiday_date', { ascending: true });
      if (error) throw error;
      setHolidays(data || []);
    } catch (err) {
      addNotification('Error al cargar feriados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfessionals = async () => {
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('full_name')
        .order('full_name', { ascending: true });
      if (error) throw error;
      
      const filteredProfs = data?.filter(p => p.full_name !== 'ADMINISTRACION GDT') || [];
      setProfessionals(filteredProfs);
      
      if (filteredProfs.length > 0) {
        setNewHoliday(prev => ({ ...prev, professional_name: filteredProfs[0].full_name }));
      }
    } catch (err) {
      console.error('Error al cargar profesionales para bloqueos:', err);
    }
  };

  useEffect(() => {
    fetchHolidays();
    fetchProfessionals();
  }, []);

  const handleAddHoliday = async () => {
    if (!newHoliday.date) {
      addNotification('La fecha es obligatoria', 'warning');
      return;
    }
    if (newHoliday.scope === 'professional' && !newHoliday.professional_name) {
      addNotification('Debe seleccionar un profesional para el bloqueo', 'warning');
      return;
    }
    try {
      const { error } = await supabase
        .from('holidays')
        .insert([
          { 
            holiday_date: newHoliday.date, 
            description: newHoliday.description,
            scope: newHoliday.scope,
            professional_name: newHoliday.scope === 'professional' ? newHoliday.professional_name : null
          }
        ]);
      if (error) throw error;
      addNotification('Bloqueo registrado con éxito', 'success');
      setNewHoliday(prev => ({ 
        ...prev, 
        date: '', 
        description: '', 
        scope: 'general',
        professional_name: professionals[0]?.full_name || '' 
      }));
      fetchHolidays();
    } catch (err) {
      addNotification('Error al agregar bloqueo: ' + err.message, 'error');
    }
  };

  const handleDeleteHoliday = async (id) => {
    if (!window.confirm('¿Eliminar este bloqueo de agenda?')) return;
    try {
      const { error } = await supabase.from('holidays').delete().eq('id', id);
      if (error) throw error;
      addNotification('Bloqueo eliminado', 'info');
      fetchHolidays();
    } catch (err) {
      addNotification('Error al eliminar', 'error');
    }
  };

  return (
    <div className="settings-section animate-fade">
      <div className="flex-between">
        <div>
          <h3>Feriados, Vacaciones y Bloqueos de Agenda</h3>
          <p className="section-desc">Gestioná los días no laborables generales o las ausencias específicas de profesionales.</p>
        </div>
      </div>

      <div className="holiday-form-card card glass-panel-premium" style={{ padding: '1.5rem', marginBottom: '2rem', border: '1px solid rgba(255,255,255,0.05)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', alignItems: 'end' }}>
          <div className="form-group">
            <label>Fecha del Bloqueo</label>
            <input 
              type="date" 
              value={newHoliday.date} 
              style={{ width: '100%' }}
              onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Alcance / Tipo de Bloqueo</label>
            <select
              value={newHoliday.scope}
              onChange={(e) => setNewHoliday({...newHoliday, scope: e.target.value})}
              style={{ width: '100%', padding: '0.6rem', background: '#1c1c24', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', height: '40px' }}
            >
              <option value="general">Clínica Completa (Feriado General)</option>
              <option value="professional">Por Profesional / Especialidad</option>
            </select>
          </div>

          {newHoliday.scope === 'professional' && (
            <div className="form-group animate-fade-in">
              <label>Profesional / Especialidad</label>
              <select
                value={newHoliday.professional_name}
                onChange={(e) => setNewHoliday({...newHoliday, professional_name: e.target.value})}
                style={{ width: '100%', padding: '0.6rem', background: '#1c1c24', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', height: '40px' }}
              >
                {professionals.map(p => (
                  <option key={p.full_name} value={p.full_name}>{p.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group" style={{ gridColumn: newHoliday.scope === 'professional' ? 'span 1' : 'span 1' }}>
            <label>Motivo / Descripción</label>
            <input 
              type="text" 
              placeholder="Ej: Feriado Nacional, Vacaciones, Congreso, etc."
              value={newHoliday.description}
              style={{ width: '100%' }}
              onChange={(e) => setNewHoliday({...newHoliday, description: e.target.value})}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn-primary" onClick={handleAddHoliday} style={{ height: '42px', padding: '0 1.5rem', width: '100%' }}>
              <Plus size={18} /> AGREGAR BLOQUEO
            </button>
          </div>
        </div>
      </div>

      <div className="holidays-list">
        {loading ? (
          <div className="text-center-p">Procesando calendario de bloqueos...</div>
        ) : holidays.length === 0 ? (
          <div className="no-data-msg">No hay bloqueos ni feriados registrados.</div>
        ) : (
          <div className="holidays-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
            {holidays.map(h => (
              <div key={h.id} className="holiday-item card glass-panel" style={{ padding: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(255,255,255,0.03)' }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <div className="holiday-icon-box" style={{ background: h.scope === 'professional' ? 'rgba(0, 229, 255, 0.1)' : 'rgba(255, 82, 82, 0.1)', color: h.scope === 'professional' ? '#00e5ff' : '#ff5252', padding: '0.75rem', borderRadius: '10px' }}>
                    <CalendarIcon size={20} />
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', color: 'white', fontSize: '1rem' }}>
                      {format(new Date(h.holiday_date + 'T00:00:00'), "EEEE d 'de' MMMM", { locale: es }).toUpperCase()}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span>{h.description || 'Sin descripción'}</span>
                      {h.scope === 'professional' ? (
                        <span style={{ alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '12px', background: 'rgba(0, 229, 255, 0.1)', color: '#00e5ff' }}>
                          💼 {h.professional_name}
                        </span>
                      ) : (
                        <span style={{ alignSelf: 'flex-start', fontSize: '0.7rem', fontWeight: 'bold', padding: '2px 8px', borderRadius: '12px', background: 'rgba(255, 82, 82, 0.1)', color: '#ff5252' }}>
                          🌍 GENERAL (TODO EL CONSULTORIO)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button className="btn-report-action delete" onClick={() => handleDeleteHoliday(h.id)} title="Eliminar">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Holidays;
